import axios, { AxiosError, AxiosResponse } from 'axios';
import { INFLIGHT_URL } from 'services/apis/config';
import { get } from 'lodash';
import { showToast } from 'components/commun/Toaster/resources/actions';

let isRefreshing = false;
let failedQueue: any[] = [];

interface ResponseData {
  success: boolean;
  status: number;
  error: any;
}

// Function to process the queued requests
const processQueue = (error: any, token: string = '') => {
  failedQueue.forEach((prom: any) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Create Axios instance
const axiosInstance = axios.create({
  baseURL: INFLIGHT_URL,
});

// Add Authorization header to all requests via request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken'); // Get token before each request
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`; // Inject token into headers
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle responses and errors, including token refresh logic
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    const { success, status } = get(response, 'data') as ResponseData;

    if (!success && (status === 503 || status === 501 || status === 403 || status === 401)) {
      showToast({
        message: 'Something went wrong, please try again later',
        type: 'error',
      });
      localStorage.clear();
      window.location.href = '/login';
    }

    if (!success && (status === 500 || status === 502)) {
      localStorage.clear();
      window.location.href = '/login';
    }

    return response;
  },
  (error: AxiosError) => {
    const originalRequest = error.config;

    const handleRefresh = () => {
      if (!isRefreshing) {
        isRefreshing = true;
        const refresh = localStorage.getItem('refreshToken');

        if (!refresh) {
          localStorage.clear();
          window.location.href = '/login';
          return Promise.reject('No refresh token found');
        }

        return axiosInstance
          .get(`${INFLIGHT_URL}/api/rbac/authenticate/refresh?refreshToken=${refresh}`)
          .then((response) => {
            isRefreshing = false;
            if (response.status === 200 && response.data.token) {
              localStorage.setItem('accessToken', response.data.token.accessToken); // Update token in localStorage
              processQueue(null, response.data.token.accessToken); // Process the queued requests with the new token
              return response.data.token.accessToken;
            } else {
              localStorage.clear();
              window.location.href = '/login';
              return Promise.reject('Failed to refresh token.');
            }
          })
          .catch((error) => {
            processQueue(error); // Reject all queued requests in case of error
            failedQueue = []; // Clear the queue
            localStorage.clear();
            window.location.href = '/login';
            return Promise.reject(error);
          })
          .finally(() => {
            isRefreshing = false;
          });
      } else {
        // Queue the request if refresh is already in progress
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((newToken) => {
          if (originalRequest) {
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`; // Retry the original request with the new token
            return axiosInstance(originalRequest);
          } else {
            return Promise.reject(new Error('Original request is undefined'));
          }
        });
      }
    };

    // Handle specific errors like 401/403 for token refresh
    if (error?.response?.status === 401 || error?.response?.status === 403 || error?.response?.status === 503) {
      if (originalRequest && originalRequest.url && originalRequest.url.includes('/rbac/authenticate/refresh')) {
        localStorage.clear();
        window.location.href = '/login';
        return Promise.reject(error); // If refresh itself failed, reject and log out
      }

      return handleRefresh(); // Attempt token refresh
    } else if (!error.response) {
      // Handle network errors (no internet connection)
      showToast({
        message: 'Internet is unavailable at the moment',
        type: 'error',
      });
      localStorage.clear();
      window.location.href = '/login';
      return Promise.reject(new Error('Network error: Internet is unavailable'));
    } else {
      return Promise.reject(error);
    }
  }
);

export default axiosInstance;